import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Plus, Trash2, Zap, DollarSign, Package, AlertTriangle, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { resolveCategory } from "@/lib/categoryTaxonomy";

interface BasketItem {
  id: string;
  name: string;
  category: string | null;
  result?: BasketItemResult;
  loading?: boolean;
}

interface BasketItemResult {
  productName: string;
  price: string;
  priceNumeric: number;
  retailer: string;
  deliveryTime: string;
  confidence: string;
}

type OptimizationView = "best_overall" | "fastest" | "consolidated";

function groupByRetailer(items: BasketItem[]): Record<string, BasketItem[]> {
  const groups: Record<string, BasketItem[]> = {};
  for (const item of items) {
    const key = item.result?.retailer || "Unknown";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}

export default function BasketPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<BasketItem[]>([]);
  const [input, setInput] = useState("");
  const [view, setView] = useState<OptimizationView>("best_overall");
  const [optimizing, setOptimizing] = useState(false);
  const [optimized, setOptimized] = useState(false);

  const addItem = () => {
    if (!input.trim()) return;
    const resolved = resolveCategory(input.trim());
    setItems(prev => [...prev, {
      id: crypto.randomUUID(),
      name: input.trim(),
      category: resolved?.clusterId || null,
    }]);
    setInput("");
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setOptimized(false);
  };

  const optimize = async () => {
    if (items.length === 0) return;
    setOptimizing(true);
    setOptimized(false);

    // Run each item through the recommend function individually
    const updated = [...items];
    for (let i = 0; i < updated.length; i++) {
      updated[i] = { ...updated[i], loading: true };
      setItems([...updated]);

      try {
        const { data } = await supabase.functions.invoke("recommend", {
          body: { query: updated[i].name, market: "US", currency: "USD" },
        });

        const winner = data?.decisions?.[0];
        if (winner) {
          updated[i] = {
            ...updated[i],
            loading: false,
            result: {
              productName: winner.productName || updated[i].name,
              price: winner.price || "N/A",
              priceNumeric: parseFloat((winner.price || "0").replace(/[^0-9.]/g, "")) || 0,
              retailer: winner.verifiedOffers?.[0]?.retailer || "Best available",
              deliveryTime: winner.deliveryTime || "Standard",
              confidence: winner.confidence || "estimated",
            },
          };
        } else {
          updated[i] = { ...updated[i], loading: false };
        }
      } catch {
        updated[i] = { ...updated[i], loading: false };
      }
      setItems([...updated]);
    }

    setOptimizing(false);
    setOptimized(true);
  };

  const itemsWithResults = items.filter(i => i.result);
  const totalCost = itemsWithResults.reduce((sum, i) => sum + (i.result?.priceNumeric || 0), 0);
  const retailerGroups = groupByRetailer(itemsWithResults);
  const retailerCount = Object.keys(retailerGroups).length;

  const views: { key: OptimizationView; label: string; icon: typeof DollarSign }[] = [
    { key: "best_overall", label: "Best overall", icon: DollarSign },
    { key: "fastest", label: "Fastest", icon: Zap },
    { key: "consolidated", label: "Fewest retailers", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-background pt-safe pb-[100px] md:pb-[72px]">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <h1 className="text-xl font-display font-bold text-foreground mb-1">Basket</h1>
        <p className="text-sm text-muted-foreground font-body mb-5">
          Group items to find the smartest way to buy them together.
        </p>

        {/* Add items */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addItem()}
              placeholder="Add a product (e.g. running shoes)"
              className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-border/50 bg-card text-sm font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/30"
            />
          </div>
          <button
            onClick={addItem}
            disabled={!input.trim()}
            className="px-3 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-body font-medium disabled:opacity-40"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Item list */}
        {items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="w-6 h-6 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-body text-muted-foreground mb-1">Your basket is empty</p>
            <p className="text-xs font-body text-muted-foreground/60">Add 2+ items to find the best way to buy them.</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5 mb-4">
              <AnimatePresence>
                {items.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex items-center justify-between rounded-xl border border-border/40 bg-card px-3.5 py-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-body text-foreground truncate">{item.name}</p>
                      {item.category && (
                        <span className="text-[9px] text-muted-foreground/60 font-body capitalize">{item.category}</span>
                      )}
                      {item.loading && (
                        <span className="text-[10px] text-primary/70 font-body ml-2">Analyzing...</span>
                      )}
                      {item.result && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] font-body font-medium text-foreground">{item.result.price}</span>
                          <span className="text-[10px] text-muted-foreground font-body">{item.result.retailer}</span>
                          <span className="text-[10px] text-muted-foreground/60 font-body">{item.result.deliveryTime}</span>
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeItem(item.id)} className="p-1.5 hover:bg-muted/30 rounded-lg transition-colors">
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Optimize button */}
            <button
              onClick={optimize}
              disabled={items.length < 2 || optimizing}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-body font-medium text-sm disabled:opacity-40 mb-4"
            >
              {optimizing ? "Analyzing items..." : `Optimize ${items.length} items`}
            </button>

            {/* V1 honest limitation note */}
            <p className="text-[10px] text-muted-foreground/50 font-body text-center mb-4">
              Each item analyzed individually. Retailer grouping suggests consolidation opportunities.
            </p>

            {/* Results */}
            {optimized && itemsWithResults.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                {/* View tabs */}
                <div className="flex gap-1.5 mb-4">
                  {views.map((v) => (
                    <button
                      key={v.key}
                      onClick={() => setView(v.key)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-body transition-colors ${
                        view === v.key
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "bg-muted/20 text-muted-foreground border border-border/30 hover:border-primary/15"
                      }`}
                    >
                      <v.icon className="w-3 h-3" />
                      {v.label}
                    </button>
                  ))}
                </div>

                {/* Summary */}
                <div className="rounded-xl border border-border/50 bg-card p-4 mb-3">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-lg font-display font-bold text-foreground">${totalCost.toFixed(2)}</p>
                      <p className="text-[10px] text-muted-foreground font-body">Total</p>
                    </div>
                    <div>
                      <p className="text-lg font-display font-bold text-foreground">{itemsWithResults.length}</p>
                      <p className="text-[10px] text-muted-foreground font-body">Items</p>
                    </div>
                    <div>
                      <p className="text-lg font-display font-bold text-foreground">{retailerCount}</p>
                      <p className="text-[10px] text-muted-foreground font-body">Retailers</p>
                    </div>
                  </div>
                </div>

                {/* Retailer groups */}
                {view === "consolidated" && retailerCount > 1 && (
                  <div className="rounded-xl border border-score-mid/20 bg-score-mid/5 p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-score-mid shrink-0 mt-0.5" />
                      <p className="text-[11px] text-score-mid font-body">
                        Items span {retailerCount} retailers. Consolidating to fewer may save on shipping.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {Object.entries(retailerGroups).map(([retailer, groupItems]) => (
                    <div key={retailer} className="rounded-xl border border-border/40 bg-card p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-body font-medium text-foreground">{retailer}</span>
                        <span className="text-[10px] text-muted-foreground font-body">
                          {groupItems.length} {groupItems.length === 1 ? "item" : "items"}
                        </span>
                      </div>
                      {groupItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between py-1 border-t border-border/20">
                          <span className="text-[11px] font-body text-muted-foreground truncate flex-1">{item.result?.productName || item.name}</span>
                          <span className="text-[11px] font-body font-medium text-foreground ml-2">{item.result?.price}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-border/30">
                        <span className="text-[10px] font-body text-muted-foreground">Subtotal</span>
                        <span className="text-xs font-body font-semibold text-foreground">
                          ${groupItems.reduce((s, i) => s + (i.result?.priceNumeric || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
